import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = {
  key: 0,
  class: "absolute left-0 top-full z-[200] mt-0.5 text-xs text-red-600 bg-white border border-red-200 rounded px-1.5 py-0.5 shadow-md whitespace-nowrap pointer-events-none",
  role: "alert"
}
const _hoisted_3 = {
  key: 0,
  class: "mt-2 flex flex-wrap gap-2"
}
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "font-medium text-black whitespace-nowrap" }
const _hoisted_6 = {
  key: 0,
  class: "text-xs text-gray-500 whitespace-nowrap"
}
const _hoisted_7 = ["onClick"]
const _hoisted_8 = {
  key: 1,
  class: "mt-3 space-y-2"
}
const _hoisted_9 = { class: "flex items-center gap-2 flex-shrink-0 w-28" }
const _hoisted_10 = { class: "text-xs font-medium text-black truncate" }
const _hoisted_11 = { class: "flex flex-wrap gap-2" }
const _hoisted_12 = { class: "text-xs text-black" }
const _hoisted_13 = ["value", "onInput"]
const _hoisted_14 = {
  key: 2,
  class: "mt-2 text-sm text-black italic"
}

import { computed, ref, watch } from "vue";
import CouleursPickerModal from "@/components/mel/CouleursPickerModal.vue";
import type { CouleurSelectorItem, CouleurAPI } from "@/types/mel-components";
import { getColorSwatchStyle } from "@/utils/colorFamilies";

interface Props {
  modelValue: CouleurSelectorItem[];
  couleursList: CouleurAPI[];
  allowPrincipal?: boolean;
  taillesList?: string[];
  forceValidate?: boolean;
  /** Si true (défaut), le picker ne propose que les couleurs officielles. */
  officialOnly?: boolean;
  /** Libellés Shopify liés par id couleur officielle. */
  linkedSourceLabelsByColorId?: Record<number, string[]>;
}

interface Emits {
  (e: "update:modelValue", value: CouleurSelectorItem[]): void;
  (e: "manage-colors"): void;
  (e: "selection-complete"): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CouleursSelector',
  props: {
    modelValue: {},
    couleursList: {},
    allowPrincipal: { type: Boolean, default: true },
    taillesList: {},
    forceValidate: { type: Boolean, default: false },
    officialOnly: { type: Boolean, default: true },
    linkedSourceLabelsByColorId: {}
  },
  emits: ["update:modelValue", "manage-colors", "selection-complete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;

const isModalOpen = ref(false);
const touched = ref(false);
const selectorRootRef = ref<HTMLElement | null>(null);

const showInvalid = computed(
  () =>
    props.modelValue.length === 0 &&
    (touched.value || props.forceValidate),
);

const onTriggerBlur = () => {
  touched.value = true;
};

const openModal = () => {
  isModalOpen.value = true;
};

const showQuantitesParTaille = computed(() => {
  return props.taillesList && props.taillesList.length > 0;
});

const linkedCaptionFor = (colorId: number | string) => {
  const id = Number(colorId);
  if (!Number.isInteger(id) || id <= 0) return "";
  const labels = props.linkedSourceLabelsByColorId?.[id] ?? [];
  if (!labels.length) return "";
  return `liée : ${labels.join(", ")}`;
};

const onModalConfirm = (colors: CouleurAPI[]) => {
  const previousById = new Map(
    props.modelValue.map((c) => [parseInt(String(c.id), 10), c]),
  );

  const updated = colors.map((couleur) => {
    const id = parseInt(String(couleur.id_couleur), 10);
    const prev = previousById.get(id);
    if (prev) return { ...prev };

    return {
      id,
      nom: couleur.couleur_FR,
      isMain: false,
      hexColor: couleur.hexColor || null,
      quantitesParTaille:
        showQuantitesParTaille.value && props.taillesList
          ? new Array(props.taillesList.length).fill(0)
          : undefined,
    };
  });

  if (updated.length > 0 && !updated.some((c) => c.isMain)) {
    updated[0].isMain = true;
  }

  emit("update:modelValue", updated);
  emit("selection-complete");
};

const focusFirstQuantity = () => {
  const firstQuantityInput =
    selectorRootRef.value?.querySelector<HTMLInputElement>(
      "[data-color-quantity]",
    );
  firstQuantityInput?.focus();
  firstQuantityInput?.select();
  return Boolean(firstQuantityInput);
};

__expose({ focusFirstQuantity });

const setCouleurPrincipale = (index: number) => {
  const updatedCouleurs = props.modelValue.map((couleur, i) => ({
    ...couleur,
    isMain: i === index,
  }));
  emit("update:modelValue", updatedCouleurs);
};

const removeCouleur = (index: number) => {
  const wasMain = props.modelValue[index].isMain;
  const updatedCouleurs = [...props.modelValue];
  updatedCouleurs.splice(index, 1);

  if (wasMain && updatedCouleurs.length > 0) {
    updatedCouleurs[0].isMain = true;
  }

  emit("update:modelValue", updatedCouleurs);
};

const getQuantiteParTaille = (
  couleur: CouleurSelectorItem,
  tailleIndex: number,
): number => {
  if (
    !couleur.quantitesParTaille ||
    !Array.isArray(couleur.quantitesParTaille)
  ) {
    return 0;
  }
  return couleur.quantitesParTaille[tailleIndex] || 0;
};

const updateQuantiteParTaille = (
  couleurIndex: number,
  tailleIndex: number,
  event: Event,
) => {
  const target = event.target as HTMLInputElement;
  const value = parseInt(target.value) || 0;

  if (value < 0) {
    target.value = "0";
    return;
  }

  const updatedCouleurs = [...props.modelValue];
  const couleur = updatedCouleurs[couleurIndex];

  if (!couleur.quantitesParTaille && props.taillesList) {
    couleur.quantitesParTaille = new Array(props.taillesList.length).fill(0);
  }

  if (couleur.quantitesParTaille) {
    couleur.quantitesParTaille[tailleIndex] = value;
  }

  emit("update:modelValue", updatedCouleurs);
};

watch(
  () => props.taillesList,
  (newTailles, oldTailles) => {
    if (!showQuantitesParTaille.value) return;

    if (newTailles && oldTailles && newTailles.length !== oldTailles.length) {
      const updatedCouleurs = props.modelValue.map((couleur) => ({
        ...couleur,
        quantitesParTaille: newTailles
          ? new Array(newTailles.length).fill(0)
          : undefined,
      }));
      emit("update:modelValue", updatedCouleurs);
    }
  },
  { deep: true },
);

watch(
  () => showQuantitesParTaille.value,
  (shouldShow) => {
    if (shouldShow && props.taillesList && props.taillesList.length > 0) {
      const updatedCouleurs = props.modelValue.map((couleur) => {
        if (
          !couleur.quantitesParTaille ||
          couleur.quantitesParTaille.length !== props.taillesList!.length
        ) {
          return {
            ...couleur,
            quantitesParTaille: new Array(props.taillesList!.length).fill(0),
          };
        }
        return couleur;
      });
      emit("update:modelValue", updatedCouleurs);
    }
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "selectorRootRef",
    ref: selectorRootRef
  }, [
    _cache[3] || (_cache[3] = _createElementVNode("label", { class: "block text-sm font-bold text-black mb-1" }, [
      _createTextVNode(" Couleurs "),
      _createElementVNode("span", { class: "text-red-500" }, "*")
    ], -1)),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("button", {
        type: "button",
        class: _normalizeClass(["w-full flex items-center gap-2 px-3 py-2 border rounded-md bg-white text-left text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500", 
          showInvalid.value
            ? 'border-red-500 ring-1 ring-red-500'
            : 'border-gray-300'
        ]),
        onClick: openModal,
        onBlur: onTriggerBlur
      }, _cache[1] || (_cache[1] = [
        _createElementVNode("i", { class: "fas fa-search text-gray-400 text-xs" }, null, -1),
        _createElementVNode("span", { class: "text-gray-400 truncate" }, " Rechercher et ajouter des couleurs... ", -1)
      ]), 34),
      (showInvalid.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_2, " Ce champ est obligatoire "))
        : _createCommentVNode("", true)
    ]),
    (_ctx.modelValue.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.modelValue, (couleur, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: index,
              class: _normalizeClass([
          'inline-flex items-center gap-1.5 px-2.5 py-1.5 border-2 rounded-full transition-colors text-sm',
          props.allowPrincipal !== false
            ? 'cursor-pointer hover:bg-gray-100'
            : 'bg-gray-50',
          couleur.isMain && props.allowPrincipal !== false
            ? 'bg-blue-50 border-blue-500'
            : 'bg-gray-50 border-gray-200',
        ]),
              onClick: ($event: any) => (
          props.allowPrincipal !== false && !couleur.isMain
            ? setCouleurPrincipale(index)
            : null
        )
            }, [
              _createElementVNode("span", {
                class: "color-swatch-dot inline-block w-4 h-4 rounded-full flex-shrink-0",
                style: _normalizeStyle(_unref(getColorSwatchStyle)(couleur.nom, couleur.hexColor))
              }, null, 4),
              _createElementVNode("span", _hoisted_5, _toDisplayString(couleur.nom), 1),
              (linkedCaptionFor(couleur.id))
                ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(linkedCaptionFor(couleur.id)), 1))
                : _createCommentVNode("", true),
              _createElementVNode("button", {
                type: "button",
                onClick: _withModifiers(($event: any) => (removeCouleur(index)), ["stop"]),
                class: "ml-0.5 transition-colors text-red-500 hover:text-red-700",
                title: "Supprimer cette couleur"
              }, _cache[2] || (_cache[2] = [
                _createElementVNode("i", { class: "fas fa-times text-xs" }, null, -1)
              ]), 8, _hoisted_7)
            ], 10, _hoisted_4))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (
        _ctx.modelValue.length > 0 &&
        showQuantitesParTaille.value &&
        _ctx.taillesList &&
        _ctx.taillesList.length > 0
      )
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.modelValue, (couleur, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: 'qty-' + index,
              class: "flex items-center gap-3 px-2 py-1 rounded-md bg-gray-50"
            }, [
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("span", {
                  class: "color-swatch-dot inline-block w-3 h-3 rounded-full flex-shrink-0",
                  style: _normalizeStyle(_unref(getColorSwatchStyle)(couleur.nom, couleur.hexColor))
                }, null, 4),
                _createElementVNode("span", _hoisted_10, _toDisplayString(couleur.nom), 1)
              ]),
              _createElementVNode("div", _hoisted_11, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.taillesList, (taille, tailleIndex) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: tailleIndex,
                    class: "flex items-center gap-1"
                  }, [
                    _createElementVNode("label", _hoisted_12, _toDisplayString(taille), 1),
                    _createElementVNode("input", {
                      type: "number",
                      "data-color-quantity": "",
                      value: getQuantiteParTaille(couleur, tailleIndex),
                      onInput: ($event: any) => (updateQuantiteParTaille(index, tailleIndex, $event)),
                      min: "0",
                      placeholder: "0",
                      class: "w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
                    }, null, 40, _hoisted_13)
                  ]))
                }), 128))
              ])
            ]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (_ctx.modelValue.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, " Aucune couleur ajoutée. Cliquez ci-dessus pour en choisir. "))
      : _createCommentVNode("", true),
    _createVNode(CouleursPickerModal, {
      isVisible: isModalOpen.value,
      "onUpdate:isVisible": _cache[0] || (_cache[0] = ($event: any) => ((isModalOpen).value = $event)),
      couleursList: _ctx.couleursList,
      selectedIds: _ctx.modelValue.map((c) => c.id),
      "official-only": _ctx.officialOnly,
      "linked-source-labels-by-color-id": _ctx.linkedSourceLabelsByColorId,
      onConfirm: onModalConfirm
    }, null, 8, ["isVisible", "couleursList", "selectedIds", "official-only", "linked-source-labels-by-color-id"])
  ], 512))
}
}

})